// ============================================================
// creationsModal.js — Modal "Mis creaciones"
// ------------------------------------------------------------
// Se abre desde el chip de adjunto del prompt box cuando el
// usuario pulsa "Mis creaciones". Permite seleccionar imágenes
// generadas previamente (hasta 14) y devolverlas al chip que
// disparó la apertura.
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { listHistory } from './api.js';

const MAX_SELECTION = 14;

let modalEl = null;
let activeChip = null;          // chip que disparó la apertura
const selected = new Set();     // ids de creaciones seleccionadas

export function initCreationsModal() {
  modalEl = qs('#creationsModal');
  if (!modalEl) return;

  // Cerrar (X o clic fuera del diálogo)
  on(modalEl, 'click', '[data-action="creations-close"]', closeModal);

  // Toggle de selección
  on(modalEl, 'click', '.creation-thumb', (e, thumb) => {
    const id = thumb.dataset.creation;
    if (!id) return;

    if (selected.has(id)) {
      selected.delete(id);
      thumb.classList.remove('is-selected');
    } else {
      if (selected.size >= MAX_SELECTION) {
        toast({ message: `Máximo ${MAX_SELECTION} imágenes`, type: 'error' });
        return;
      }
      selected.add(id);
      thumb.classList.add('is-selected');
    }
    syncFooter();
  });

  // Confirmar y añadir al chip
  on(modalEl, 'click', '[data-action="creations-add"]', () => {
    if (selected.size === 0) return;
    applySelectionToChip();
    closeModal();
  });

  // Cerrar con Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modalEl.classList.contains('is-open')) {
      closeModal();
    }
  });
}

/**
 * Abre el modal. Recibe el chip que disparó la apertura para
 * poder devolverle las imágenes seleccionadas al confirmar.
 */
export async function openCreationsModal(chip) {
  if (!modalEl) return;
  activeChip = chip || null;

  // Resetear selección previa
  selected.clear();
  syncFooter();

  modalEl.classList.add('is-open');
  modalEl.setAttribute('aria-hidden', 'false');
  document.body.classList.add('has-modal-open');

  await populateCreationsGrid();
}

async function populateCreationsGrid() {
  const grid = qs('.creations-grid', modalEl);
  if (!grid) return;
  
  grid.innerHTML = `
    <div class="text-center py-4 w-100">
      <div class="spinner-border text-light" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-2 text-muted" style="font-size: 0.85rem;">Cargando tus imágenes...</p>
    </div>
  `;
  
  try {
    const items = await listHistory();
    // Filtrar solo imágenes completadas
    const imageItems = items.filter(item => item.status === 'completed' && item.type === 'image');
    
    grid.innerHTML = '';
    
    if (imageItems.length === 0) {
      grid.innerHTML = '<p class="text-muted w-100 text-center py-4">No tienes imágenes generadas todavía.</p>';
      return;
    }
    
    imageItems.forEach(item => {
      const btn = document.createElement('button');
      btn.className = 'creation-thumb';
      btn.type = 'button';
      btn.dataset.creation = item.id;
      btn.dataset.url = item.url;
      
      btn.innerHTML = `
        <img class="creation-thumb-img" src="${item.url}" alt="${item.title || 'Imagen'}" style="width:100%; height:100%; object-fit:cover; border-radius:8px;" />
        <span class="creation-thumb-check"><i class="bi bi-check-lg"></i></span>
      `;
      
      grid.appendChild(btn);
    });
    
  } catch (err) {
    console.error('[CreationsModal] Error al cargar creaciones:', err);
    grid.innerHTML = '<p class="text-danger w-100 text-center py-4">Error al cargar creaciones.</p>';
  }
}

function closeModal() {
  if (!modalEl) return;
  modalEl.classList.remove('is-open');
  modalEl.setAttribute('aria-hidden', 'true');
  document.body.classList.remove('has-modal-open');
  activeChip = null;
}

function syncFooter() {
  const count = qs('[data-bind="creations-count"]', modalEl);
  if (count) count.textContent = selected.size;
  const addBtn = qs('[data-action="creations-add"]', modalEl);
  if (addBtn) addBtn.disabled = selected.size === 0;
}

function applySelectionToChip() {
  if (!activeChip) {
    toast({ message: `${selected.size} imagen(es) añadidas`, type: 'success' });
    return;
  }

  // Obtener las URLs de los elementos seleccionados
  const selectedThumbs = qsa('.creation-thumb.is-selected', modalEl);
  const urls = [...selectedThumbs].map(t => t.dataset.url);

  if (urls.length > 0) {
    // Guardamos la primera URL seleccionada en el slot del chip
    activeChip._file = urls[0];

    activeChip.classList.remove('is-hidden');
    activeChip.classList.add('has-file');

    const label = qs('[data-bind="attach-label"]', activeChip);
    if (label) {
      const fileName = urls[0].split('/').pop();
      const shortName = fileName.length > 20 ? fileName.slice(0, 20) + '…' : fileName;
      label.textContent = shortName;
    }

    const icon = qs('.chip-main > i', activeChip);
    if (icon) {
      const thumb = document.createElement('img');
      thumb.src = urls[0];
      thumb.alt = '';
      thumb.className = 'chip-thumb';
      icon.replaceWith(thumb);
    }
  }

  toast({ message: `${selected.size} imagen(es) añadidas desde Mis creaciones`, type: 'success' });
}
